import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { hasPermission } from "@/lib/permissions";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Lock, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  { value: "staff_obra", label: "Staff obra" },
  { value: "staff_oficina", label: "Staff oficina central" },
  { value: "asset_admin", label: "Administrador de activos" },
  { value: "staff_admin", label: "Administrador de staff" },
  { value: "company_admin", label: "Administrador de compañía" },
];
const STATUS_OPTIONS = [
  { value: "pending", label: "En espera de autorización" },
  { value: "active", label: "Dar de alta (activo)" },
  { value: "disabled", label: "Dar de baja (inactivo)" },
];
const STATUS_STYLE = {
  pending: "text-[#92400E]", active: "text-[#047857]", disabled: "text-[#9F1239]",
};
const th = "px-3 py-2 text-left text-[10px] uppercase tracking-[0.12em] text-[#5C6A7D] font-semibold whitespace-nowrap";
const td = "px-3 py-3 text-sm text-[#0A2540] align-middle";

export default function AdminStaff() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [activateTarget, setActivateTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => api.get("/admin/staff").then(({ data }) => setRows(data));
  useEffect(() => { if (hasPermission(user, "manage_staff")) load(); }, []);

  if (user && !hasPermission(user, "manage_staff")) return <Navigate to="/" replace />;

  const changeRole = async (row, role) => {
    try { await api.patch(`/admin/staff/${row.id}`, { role }); toast.success("Rol actualizado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo actualizar"); }
  };

  const changeStatus = async (row, status) => {
    try {
      await api.patch(`/admin/staff/${row.id}`, { status });
      load();
      if (row.status === "pending" && status === "active") {
        setActivateTarget({ ...row, status });
      } else {
        toast.success("Estatus actualizado");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo actualizar"); }
  };

  const sendActivationEmail = async () => {
    if (!activateTarget) return;
    try {
      const { data } = await api.post(`/admin/staff/${activateTarget.id}/notify-activation`);
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo enviar");
    } finally { setActivateTarget(null); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/staff/${deleteTarget.id}`);
      toast.success(`Cuenta de ${deleteTarget.name} eliminada permanentemente.`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo eliminar la cuenta");
    } finally { setDeleting(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-staff-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A7D]">Administración</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tighter mt-1">Control de staff.</h1>
        <p className="text-sm text-[#5C6A7D] mt-2">Autoriza altas, ajusta roles y da de baja al personal de tu compañía.</p>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
            <tr><th className={th}>Nombre del personal</th><th className={th}>Compañía</th><th className={th}>Proyecto</th><th className={th}>Rol</th><th className={th}>Estatus</th><th className={th}>Acción</th></tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-[#5C6A7D]">Sin personal registrado.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`staff-row-${r.id}`} className={r.status === "pending" ? "bg-[#FFFBEB]/60" : ""}>
                <td className={td}>
                  <div className="flex items-center gap-2 font-semibold">
                    {r.name}
                    {r.locked && <Lock size={12} className="text-[#94A3B8]" />}
                  </div>
                  <div className="text-xs text-[#5C6A7D]">{r.email}</div>
                </td>
                <td className={td} data-testid={`staff-company-${r.id}`}>{r.company_name || "—"}</td>
                <td className={td}>{r.project || "—"}</td>
                <td className={td}>
                  <Select value={r.role} onValueChange={(v) => changeRole(r, v)} disabled={r.locked}>
                    <SelectTrigger className="h-9 rounded-sm min-w-[190px]" data-testid={`staff-role-${r.id}`}><SelectValue placeholder={r.role_label || "—"} /></SelectTrigger>
                    <SelectContent>{ROLE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className={td}>
                  <Select value={r.status} onValueChange={(v) => changeStatus(r, v)} disabled={r.locked}>
                    <SelectTrigger className={`h-9 rounded-sm min-w-[210px] font-semibold ${STATUS_STYLE[r.status]}`} data-testid={`staff-status-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className={td}>
                  <button
                    type="button"
                    disabled={r.locked}
                    onClick={() => setDeleteTarget(r)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#9F1239] hover:text-[#E11D48] hover:underline disabled:text-[#CBD5E1] disabled:hover:text-[#CBD5E1] disabled:no-underline disabled:cursor-not-allowed"
                    data-testid={`staff-delete-${r.id}`}
                  >
                    <Trash2 size={13} /> Eliminar cuenta
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!activateTarget} onOpenChange={(o) => !o && setActivateTarget(null)}>
        <AlertDialogContent data-testid="activation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Alta confirmada — ¿enviar correo?</AlertDialogTitle>
            <AlertDialogDescription>
              {activateTarget?.name} fue dado de alta. ¿Deseas enviarle un correo avisándole que su alta en Tangram fue aceptada, con el enlace de acceso?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="activation-skip">Ahora no</AlertDialogCancel>
            <AlertDialogAction onClick={sendActivationEmail} className="bg-[#0A2540] hover:bg-[#173B63]" data-testid="activation-send">Enviar correo</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && !deleting && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-staff-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar cuenta permanentemente</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a eliminar la cuenta de <b>{deleteTarget?.name}</b> ({deleteTarget?.email}). Esta acción no se puede deshacer:
              se borra el usuario, sus notificaciones, sus enlaces de activación pendientes y su participación en proyectos.
              Si prefieres que conserve el registro pero pierda acceso, usa "Dar de baja" en el estatus.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-staff-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting} className="bg-[#9F1239] hover:bg-[#E11D48]" data-testid="delete-staff-confirm">
              {deleting ? "Eliminando…" : "Eliminar cuenta"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
